import {
  AUTHENTICATION_CONNECTION,
  AuthenticationState as B2XAuthState,
  getClaims,
  getJwe,
  getUserProfile,
  IdentityProvider,
  initializeAuthentication,
  isLoggedIn,
  isPending,
  Stages,
} from '@b2x/authentication-library';
import { AppConfig } from '@dh-io-emh/config-types';
import { initializeStore } from '@seamless/store';
import { UserProfile } from './types';

export const initializeAuthConnection = ({
  config,
  tenantId,
}: {
  config: AppConfig;
  tenantId: string;
}): Promise<void> => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    if (window.location.hostname.includes('dcp-local') || window.location.hostname.includes('localhost')) {
      // Initialize authentication in standalone mode. Normally done by the AEM environment automatically
      // but for our legacy SPA and local develop it has to be done manually.
      await initializeAuthentication({
        identityProvider: IdentityProvider.CIAS,
        country: config.shopCountry,
        tenantId,
        stage: config.apiMode === 'prod' ? Stages.PROD : Stages.INT,
      });
    }

    const store = initializeStore();

    const { unsubscribe } = store.subscribe(AUTHENTICATION_CONNECTION, (state: B2XAuthState) => {
      // Wait until auth lib has initialized
      if (!isPending(state)) {
        resolve();
        setTimeout(() => unsubscribe(), 0);
      }
    });
  });
};

// Transform B2X state and handle case where an account has an incomplete profile
export const transformB2XAuthState = (state: B2XAuthState) => {
  const userProfile = getUserProfile(state);
  let profile: UserProfile | null = null;

  if (userProfile) {
    profile = {
      ...userProfile,
      fullName: `${userProfile.firstName} ${userProfile.lastName1}`,
    };
  } else if (isLoggedIn(state)) {
    // For "incomplete" MercedesMe accounts we don't get a userProfile because of missing details like address, phone etc.
    // In that case we grab the full name from the claims object. Our SPA only needs the fullName anyway.
    profile = {
      fullName: getClaims(state)?.name as string,
    };
  }

  return {
    isAuthenticated: isLoggedIn(state),
    jweToken: getJwe(state),
    userProfile: profile,
  };
};
